/**
 * Rolling FPS / frame-time measurement of the renderer's frames. Exposes both
 * the live read (`getStats()`) and a verdict (`isJanky()`) so the UI / the
 * auto-downgrade coordinator can react.
 *
 * Driving model — the watchdog measures the frames that actually render:
 * the render loop calls {@link sample} once per rendered frame with that
 * frame's delta (e.g. Pixi's `ticker.deltaMS`). It deliberately does NOT run
 * its own `requestAnimationFrame` loop: an independent rAF would keep the
 * browser's frame pipeline (compositor / vsync) awake at 60 Hz even when the
 * renderer is parked at idle — burning CPU to measure a heartbeat no one reads
 * (the coordinator only judges FPS during playback, i.e. while rendering).
 *
 * The watchdog itself doesn't tune anything — it just observes. Callers
 * decide what to do (display, suggest a lower tier, force `low` after
 * sustained jank).
 */
const SAMPLE_WINDOW = 240; // ~4s @ 60fps
const JANK_FRAME_THRESHOLD_MS = 33; // anything > 30 fps frame budget
const JANK_RATIO_THRESHOLD = 0.15; // > 15 % of recent frames over budget

export interface FpsStats {
  /** Number of frames observed in the rolling window. */
  samples: number;
  /** Median frame duration in ms. */
  median: number;
  /** 95th percentile frame duration in ms (worst recent frames). */
  p95: number;
  /** Median FPS derived from the median frame duration. */
  fps: number;
  /** Fraction of frames over `JANK_FRAME_THRESHOLD_MS`, in [0, 1]. */
  jankRatio: number;
}

export class FpsWatchdog {
  private deltas: number[] = [];

  /**
   * Record one rendered frame's duration (ms). Called by the render loop per
   * frame — see the class doc for why the watchdog is driven rather than
   * self-scheduling. Ignores non-positive deltas (e.g. the first frame after
   * the ticker wakes from a park, whose delta is meaningless).
   */
  sample(deltaMs: number): void {
    if (!(deltaMs > 0)) {
      return;
    }

    this.deltas.push(deltaMs);

    if (this.deltas.length > SAMPLE_WINDOW) {
      this.deltas.shift();
    }
  }

  getStats(): FpsStats | null {
    if (this.deltas.length < 30) {
      return null;
    }

    const sorted = [...this.deltas].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    const overBudget = sorted.filter((d) => d > JANK_FRAME_THRESHOLD_MS).length;

    return {
      samples: sorted.length,
      median,
      p95,
      fps: median > 0 ? Math.round(1000 / median) : 0,
      jankRatio: overBudget / sorted.length,
    };
  }

  isJanky(): boolean {
    const stats = this.getStats();

    if (!stats) {
      return false;
    }

    return stats.jankRatio > JANK_RATIO_THRESHOLD;
  }
}

/**
 * Process-wide singleton so the settings panel, the auto-downgrade coordinator,
 * and the render loop that feeds it (`sample`) share the same numbers. Created
 * lazily so SSR/test environments without `window` don't crash on import.
 */
let instance: FpsWatchdog | null = null;

export function getFpsWatchdog(): FpsWatchdog {
  if (!instance) {
    instance = new FpsWatchdog();
  }

  return instance;
}
